import { type ProfileMagicInputModalProps } from '#app/components/Modals/V2/GlobalModalSystem/templates/ProfileMagicInputModal.js';
import { type CommonTemplateProps } from '#app/components/Modals/V2/GlobalModalSystem/types.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { ONBOARDING_COMPLETED_URL_PARAMETER } from '#app/components/OnboardingSuccess/constants.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { useProfileMagicErrorModal } from './useProfileMagicErrorModal.js';
import { useProfileMagicMutations } from './useProfileMagicMutations.js';
import { useProfileMagicPolling } from './useProfileMagicPolling.js';
import { useProfileMagicServicesTabIndicator } from './useProfileMagicServicesTabIndicator.js';
import { captureException } from '@contra/sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

type UseProfileMagicProps = {
  entrypoint: 'bio' | 'introModal' | 'oneLiner';
  onGeneratedSuccessfully?: () => void;
  prefilledReason?: string;
  prefilledServices?: string;
  visitorCanEdit: boolean;
};

type UseProfileMagicReturn = {
  handleSkipModalIntroScreen: () => void;
  hasSeenProfileMagicModal: boolean;
  hasUsedProfileMagic: boolean;
  isOnboardingCompletedParameter: boolean;
  isProfileMagicInputModalOpen: boolean;
  openProfileMagicModal: () => void;
};

export const useProfileMagic = ({
  entrypoint,
  onGeneratedSuccessfully,
  prefilledReason,
  prefilledServices,
  visitorCanEdit,
}: UseProfileMagicProps): UseProfileMagicReturn => {
  const toast = useToast();
  const navigation = useNavigation();
  const location = useLocation();

  const [urlSearchParameters] = useURLParameters('*');
  const isOnboardingCompletedParameter =
    urlSearchParameters['onboarding-completed'];

  // This is a short circut search param to force a specific error when attempting togenerate profile magic suggestions.
  // this is useful to test the error handling of the profile magic modal without having to go through the entire flow.
  const [forceTestError, setForceTestError] = useState(
    urlSearchParameters['profileMagicTestError'],
  );

  const hasUsedProfileMagic = useHasUserInterfaceFlags(
    'HIDE_FTUE_CONTRA_PROFILE_MAGIC',
  );
  const hasSeenProfileMagicModal = useHasUserInterfaceFlags(
    'SEEN_PROFILE_MAGIC_MODAL',
  );
  const hasSeenProfileMagicRevealAnimation = useHasUserInterfaceFlags(
    'HIDE_FTUE_CONTRA_PROFILE_MAGIC_REVEAL_ANIMATION',
  );

  const [currentBioRequestId, setCurrentBioRequestId] = useState<
    null | string
  >();
  const [currentOneLinerRequestId, setCurrentOneLinerRequestId] = useState<
    null | string
  >();

  const [wereServicesGenerated, setWereServicesGenerated] = useState(false);

  const { addUIFlag, startGeneratingProfileSuggestions } =
    useProfileMagicMutations();

  const { showProfileMagicServiceIndicator } =
    useProfileMagicServicesTabIndicator();

  const { setIsPollingWithTimeout, visitor } = useProfileMagicPolling({
    visitorCanEdit,
  });

  const [skipModalIntroScreen, setSkipModalIntroScreen] = useState(false);
  const handleSkipModalIntroScreen = useCallback(() => {
    setSkipModalIntroScreen(true);
  }, []);

  const [lastInput, setLastInput] = useState<null | {
    whatMakesYouUnique: string;
    whatServicesDoYouOffer: string;
  }>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const areBiosGenerated =
    typeof currentBioRequestId === 'string' &&
    visitor?.userAccount?.contraMagicBioSuggestions &&
    visitor.userAccount.contraMagicBioSuggestions.length > 0 &&
    visitor.userAccount.contraMagicBioSuggestions.every(
      (suggestion) => suggestion.requestId === currentBioRequestId,
    );

  const areOneLinersGenerated =
    typeof currentOneLinerRequestId === 'string' &&
    visitor?.userAccount?.contraMagicOneLinerSuggestions &&
    visitor.userAccount.contraMagicOneLinerSuggestions.length > 0 &&
    visitor.userAccount.contraMagicOneLinerSuggestions.every(
      (suggestion) => suggestion.requestId === currentOneLinerRequestId,
    );

  // projects and services will only ever be generated once, so we don't need to poll for the specific requestId. We know to stop when we have some projects that are suggestions
  const areProjectsGenerated =
    visitor?.userAccount?.profile?.portfolioProjects?.edges &&
    visitor.userAccount.profile.portfolioProjects.edges.some((suggestion) =>
      Boolean(suggestion.node.llmSuggestionId),
    );
  const areServicesGenerated =
    visitor?.userAccount?.profile?.productizedServices?.edges &&
    visitor.userAccount.profile.productizedServices.edges.some((suggestion) =>
      Boolean(suggestion.node.llmSuggestionId),
    );

  const markAnimationAsSeen = useCallback(() => {
    if (!hasSeenProfileMagicRevealAnimation) {
      addUIFlag({
        onCompleted: () => {},
        onError: () => {},
        variables: {
          input: {
            userInterfaceFlag:
              'HIDE_FTUE_CONTRA_PROFILE_MAGIC_REVEAL_ANIMATION',
          },
        },
      });
    }
  }, [addUIFlag, hasSeenProfileMagicRevealAnimation]);

  const handlePollingCleanup = useCallback(() => {
    setIsPollingWithTimeout(false, null);
  }, [setIsPollingWithTimeout]);

  const [hasRateLimitError, setHasRateLimitError] = useState(false);

  const startGeneratingSuggestions = useCallback(
    (
      reason: string,
      services: string,
      onError: (errorType: 'OTHER' | 'RATE_LIMIT' | null) => void,
    ) => {
      // short circuit to test error handling
      const forceUserRateLimitError = forceTestError === 'USER_RATE_LIMIT';
      const forceRateLimitError = forceTestError === 'RATE_LIMIT';
      const forceOtherError = forceTestError === 'OTHER';
      const forceApiError = forceUserRateLimitError || forceRateLimitError;
      const forceError = forceApiError || forceOtherError;
      // reset the test error so second attempts can work normally
      setForceTestError(undefined);

      startGeneratingProfileSuggestions({
        onCompleted: (errors, response) => {
          if (errors.hasErrors || forceError) {
            const hasApiErrors =
              forceApiError ||
              (errors.apiErrors && errors.apiErrors.length > 0);

            if (hasApiErrors) {
              const hasUserRateLimitError =
                forceUserRateLimitError ||
                Boolean(
                  errors.apiErrors?.some(
                    (error) =>
                      error.__typename === 'ContraMagicUserRateLimitError',
                  ),
                );

              const hasGeneralRateLimitError = Boolean(
                errors.apiErrors?.some(
                  (error) =>
                    error.__typename === 'ContraMagicServiceRateLimitError',
                ),
              );

              setHasRateLimitError(hasUserRateLimitError);

              if (hasUserRateLimitError) {
                toast.error(
                  'You’ve hit your daily limit, please try again later.',
                );
              } else if (hasGeneralRateLimitError) {
                onError('RATE_LIMIT');
              } else {
                onError('OTHER');
              }
            } else {
              onError('OTHER');
            }
          } else {
            const maybeBioRequestId =
              response?.generateContraMagicProfileSuggestions?.bioRequestId;
            const maybeOneLinerRequestId =
              response?.generateContraMagicProfileSuggestions
                ?.oneLinerRequestId;
            const maybeServiceRequestId =
              response?.generateContraMagicProfileSuggestions?.serviceRequestId;

            setCurrentBioRequestId(maybeBioRequestId);
            setCurrentOneLinerRequestId(maybeOneLinerRequestId);

            // this shouldn't happen as bios and one-liners will always be generated, and it would only be undefined if an error occurred, but that case should always already be handled above.
            if (!maybeBioRequestId || !maybeOneLinerRequestId) {
              onError('OTHER');
              captureException(
                `Unable to start polling for profile magic suggestions. Missing bioRequestId or oneLinerRequestId. bioRequestId=${maybeBioRequestId}, oneLinerRequestId=${maybeOneLinerRequestId}`,
              );
              return;
            }

            setIsPollingWithTimeout(true, () => onError('OTHER'));

            // this will be used once all suggestions are generated to determine if the services tab sparkle emoji should be shown.
            if (maybeServiceRequestId) {
              setWereServicesGenerated(true);
            }
          }
        },
        onError: () => {
          onError('OTHER');
        },
        variables: {
          input: {
            whatMakesYouUnique: reason,
            whatServicesDoYouOffer: services,
          },
        },
      });
    },
    [
      forceTestError,
      setIsPollingWithTimeout,
      startGeneratingProfileSuggestions,
      toast,
    ],
  );

  const [profileMagicError, setProfileMagicError] = useState<
    'OTHER' | 'RATE_LIMIT' | null
  >(null);

  const handleSubmit = useCallback(
    (
      reason: string,
      services: string,
      onError: (errorType: 'OTHER' | 'RATE_LIMIT' | null) => void,
    ) => {
      setIsSubmitting(true);
      setLastInput({
        whatMakesYouUnique: reason,
        whatServicesDoYouOffer: services,
      });
      setWereServicesGenerated(false);

      startGeneratingSuggestions(reason, services, onError);
    },
    [startGeneratingSuggestions],
  );

  const onFailureOrTimedOut = useCallback(
    (errorType: 'OTHER' | 'RATE_LIMIT' | null) => {
      // if an error occurs, chances are we have generated at least some content,
      // so to avoid the animations playing multiple times, we will still mark the animation as seen.
      markAnimationAsSeen();

      handlePollingCleanup();
      setProfileMagicError(errorType);
    },
    [handlePollingCleanup, markAnimationAsSeen],
  );

  const profileMagicModalProps: Omit<
    ProfileMagicInputModalProps,
    keyof CommonTemplateProps
  > = {
    isSubmitting,
    onSubmit: ({
      cleanup,
      reason,
      services,
    }: {
      cleanup: () => void;
      reason: string;
      services: string;
    }) => {
      cleanup();
      handleSubmit(reason, services, onFailureOrTimedOut);
    },
    prefilledReason,
    prefilledServices,
    skipModalIntroScreen,
  };

  const {
    closeModal: closeProfileMagicModal,
    isModalOpen: isProfileMagicInputModalOpen,
    openModal: openProfileMagicModal,
  } = useModal({
    identifier: 'ProfileMagicInputModal',
    modalProps: profileMagicModalProps,
  });

  const openModal = useCallback(() => {
    openProfileMagicModal();

    if (!hasSeenProfileMagicModal) {
      addUIFlag({
        onCompleted: () => {},
        onError: () => {},
        variables: {
          input: {
            userInterfaceFlag: 'SEEN_PROFILE_MAGIC_MODAL',
          },
        },
      });
    }
  }, [addUIFlag, hasSeenProfileMagicModal, openProfileMagicModal]);

  const closeModal = useCallback(() => {
    closeProfileMagicModal();

    // if the user has the onboarding completed parameter, we remove it when they close the modal so they don't automatically see it again if they refresh the page.
    if (isOnboardingCompletedParameter) {
      navigation.replace(
        removeSearchParameter(
          location.path,
          ONBOARDING_COMPLETED_URL_PARAMETER,
        ),
      );
    }

    setIsPollingWithTimeout(false, null);
  }, [
    closeProfileMagicModal,
    isOnboardingCompletedParameter,
    location.path,
    navigation,
    setIsPollingWithTimeout,
  ]);

  // when a user cancels or closes the error modal
  // error modal can show when there is a user rate limit, general rate limit, or other error
  const handleDismissError = useCallback(() => {
    handlePollingCleanup();
    setProfileMagicError(null);
    setIsSubmitting(false);
    closeProfileMagicModal();
  }, [closeProfileMagicModal, handlePollingCleanup]);

  const { handleErrorModalOpen } = useProfileMagicErrorModal({
    closeMainModal: closeModal,
    handlePollingCleanup,
    handleSubmit,
    lastInput,
    onDismissError: handleDismissError,
    openMainModal: openModal,
  });

  const onRequestsCompleted = useCallback(() => {
    setIsSubmitting(false);
    closeProfileMagicModal();
  }, [closeProfileMagicModal]);

  const onPolledDataUpdated = useCallback(() => {
    if (
      areBiosGenerated &&
      areOneLinersGenerated &&
      areProjectsGenerated &&
      areServicesGenerated
    ) {
      setIsPollingWithTimeout(false, null);
      setCurrentBioRequestId(null);
      setCurrentOneLinerRequestId(null);

      onRequestsCompleted();

      if (!hasSeenProfileMagicRevealAnimation) {
        // we do this in a timeout to leave enough time for the 3 seconds long animation to finish playing.
        setTimeout(() => {
          markAnimationAsSeen();
        }, 3_000);
      }

      // if we are only generating bio or one liner suggestions, we want to call the onGeneratedSuccessfully callback which will open the bio or one liner suggestions popover
      if (entrypoint === 'bio' || entrypoint === 'oneLiner') {
        onGeneratedSuccessfully?.();
      }

      // if we've generated service suggestions, show the service indicator
      if (wereServicesGenerated && areServicesGenerated) {
        showProfileMagicServiceIndicator();
      }
    }
  }, [
    areBiosGenerated,
    areOneLinersGenerated,
    areProjectsGenerated,
    areServicesGenerated,
    entrypoint,
    hasSeenProfileMagicRevealAnimation,
    markAnimationAsSeen,
    onGeneratedSuccessfully,
    onRequestsCompleted,
    setIsPollingWithTimeout,
    showProfileMagicServiceIndicator,
    wereServicesGenerated,
  ]);

  // side-effects
  useEffect(() => {
    if (visitorCanEdit) {
      if (profileMagicError === 'RATE_LIMIT') {
        handleErrorModalOpen('RATE_LIMIT');
      } else if (profileMagicError === 'OTHER') {
        handleErrorModalOpen('OTHER');
      }

      setProfileMagicError(null);
    }
  }, [handleErrorModalOpen, profileMagicError, visitorCanEdit]);

  useEffect(() => {
    if (visitorCanEdit && hasRateLimitError) {
      closeModal();
    }
  }, [closeModal, hasRateLimitError, visitorCanEdit]);

  // watch for when the desired suggestions have been generated
  useEffect(() => {
    if (visitor?.userAccount) {
      onPolledDataUpdated();
    }

    // we only want to run this effect when the visitor is updated, i.e when we our polling returns new data
  }, [visitor?.userAccount]);

  return useMemo(
    () =>
      visitorCanEdit
        ? {
            closeProfileMagicModal: closeModal,
            handleSkipModalIntroScreen,
            hasSeenProfileMagicModal,
            hasUsedProfileMagic,
            isOnboardingCompletedParameter,
            isProfileMagicInputModalOpen,
            openProfileMagicModal: openModal,
          }
        : {
            handleSkipModalIntroScreen: () => {},
            hasSeenProfileMagicModal: false,
            hasUsedProfileMagic: false,
            isOnboardingCompletedParameter: false,
            isProfileMagicInputModalOpen: false,
            openProfileMagicModal: () => {},
          },
    [
      closeModal,
      handleSkipModalIntroScreen,
      hasSeenProfileMagicModal,
      hasUsedProfileMagic,
      isOnboardingCompletedParameter,
      isProfileMagicInputModalOpen,
      openModal,
      visitorCanEdit,
    ],
  );
};
